import * as XLSX from "xlsx";

const EXCLUDED_CATEGORY_KEYS = new Set([
  "campaign_images",
  "images",
  "videos",
  "scene_frames",
  "scene_images",
  "jobs",
]);

const TABLE_CATEGORY_KEYS = new Set([
  "campaign_briefs",
  "customer_personas",
  "campaign_concepts",
  "storyboards",
  "scenes",
]);

const EXCLUDED_COLUMNS = new Set([
  "id",
  "userId",
  "sessionId",
  "source",
  "createdAt",
  "updatedAt",
  "imagePath",
  "videoPath",
  "gcs_uri",
  "videoId",
]);

const SCENE_EXCLUDED_COLUMNS = new Set([
  "cameraDetails",
  "technical",
  "screenplay",
  "video",
  "environment",
  "temporalElements",
  "sequence",
  "visualEffects",
  "characters",
  "camera",
  "visualStyleAndAesthetics",
  "audio",
  "plot",
  "plotSummary",
  "storyboard_id",
  "videoId",
]);

const prettify = (value = "") =>
  String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const safeSheetName = (name = "Sheet") =>
  String(name).replace(/[\\/*?:[\]]/g, "").slice(0, 31);

const isPrimitive = (val) =>
  ["string", "number", "boolean"].includes(typeof val);

const isPlainObject = (val) =>
  val != null && typeof val === "object" && !Array.isArray(val);

const getEntries = (categoryValue) => {
  return categoryValue?.items?.entries ?? [];
};

const toCellValue = (val) => {
  if (val == null) return "";
  if (Array.isArray(val)) {
    if (val.every((x) => x == null || isPrimitive(x))) {
      return val.filter((x) => x != null).join(", ");
    }
    return "";
  }
  if (isPlainObject(val)) return "";
  return String(val);
};

const getColumnsForCategory = (categoryKey, entries) => {
  const excluded = new Set(EXCLUDED_COLUMNS);

  if (categoryKey === "scenes") {
    SCENE_EXCLUDED_COLUMNS.forEach((x) => excluded.add(x));
  }

  const allKeys = Array.from(
    new Set(entries.flatMap((item) => Object.keys(item || {})))
  );

  return allKeys.filter((key) => {
    if (excluded.has(key)) return false;

    const hasUsableValue = entries.some((item) => {
      const value = item?.[key];
      if (value == null) return false;
      if (isPlainObject(value)) return false;
      if (Array.isArray(value) && !value.every((x) => x == null || isPrimitive(x))) {
        return false;
      }
      return true;
    });

    return hasUsableValue;
  });
};

const reorderColumns = (categoryKey, columns) => {
  const preferredOrders = {
    campaign_briefs: [
      "vehicle",
      "use_brand_asset",
      "brand_asset_prompt",
      "targetAudience",
      "keySellingPoints",
      "objective",
      "desiredTone",
    ],
    customer_personas: [
      "persona_name",
      "persona_bio",
    ],
    campaign_concepts: [
      "title",
      "tagline",
      "core_message",
      "emotional_hook",
      "customer_persona_id",
    ],
    storyboards: [
      "storyboardTitle",
      "campaignSummary",
      "personaSummary",
      "storySummary",
      "overallTheme",
      "customer_persona_id",
      "campaign_concept_id",
      "numScenes",
    ],
    scenes: [
      "index",
      "name",
      "description",
      "timeRange",
      "style",
      "keywords",
      "status",
      "applyBrandGuideline",
    ],
  };

  const preferred = preferredOrders[categoryKey] ?? [];
  const preferredSet = new Set(preferred);

  return [
    ...preferred.filter((key) => columns.includes(key)),
    ...columns.filter((key) => !preferredSet.has(key)),
  ];
};

const addSheet = (wb, sheetName, data) => {
  const ws = XLSX.utils.aoa_to_sheet(data);

  const maxCols = Math.max(...data.map((row) => row.length), 1);
  ws["!cols"] = Array.from({ length: maxCols }, (_, idx) => ({
    wch: idx === 0 ? 8 : idx === 1 ? 24 : 60,
  }));

  XLSX.utils.book_append_sheet(wb, ws, safeSheetName(sheetName));
};

export const BuildSessionTextsWorkbook = ({ categories }) => {
  if (!categories || typeof categories !== "object") {
    throw new Error("No categories data available");
  }

  const wb = XLSX.utils.book_new();

  const includedEntries = Object.entries(categories).filter(
    ([key]) =>
      !EXCLUDED_CATEGORY_KEYS.has(key) &&
      TABLE_CATEGORY_KEYS.has(key)
  );

  addSheet(wb, "Overview", [
    ["Category", "Items"],
    ...includedEntries.map(([key, value]) => [
      prettify(key),
      getEntries(value).length,
    ]),
  ]);

  includedEntries.forEach(([categoryKey, categoryValue]) => {
    const entries = getEntries(categoryValue);

    const columns = reorderColumns(
      categoryKey,
      getColumnsForCategory(categoryKey, entries)
    );

    const data = [
      ["No", ...columns.map(prettify)],
      ...entries.map((item, idx) => [
        idx + 1,
        ...columns.map((key) => toCellValue(item?.[key])),
      ]),
    ];

    addSheet(wb, prettify(categoryKey), data);
  });

  return wb;
};

export const ExportSessionTextsToExcel = ({
  categories,
  sessionId,
  fileName,
}) => {
  const wb = BuildSessionTextsWorkbook({ categories });
  XLSX.writeFile(
    wb,
    fileName || `session_texts_${sessionId || "session"}.xlsx`
  );
};

export const BuildSessionTextsExcelBlob = ({ categories }) => {
  const wb = BuildSessionTextsWorkbook({ categories });
  const arrayBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  return new Blob([arrayBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};